import * as THREE from "three";
import { Emitter } from "./emitters";
import { PlacedObject } from "./objects";
import { AppContext } from "./appContext";
import { EntityKind } from "./undoStack";

interface EntityIndexDeps {
  recordBefore(kind: EntityKind, id: string): void;
}

export interface EntityIndex {
  objectById(id: string): PlacedObject | null;
  emitterById(id: string): Emitter | null;
  isTransformParent(id: string): boolean;
  transformParentId(id: string): string | null;
  topmostGroupOf(id: string): string | null;
  selectedGroupContains(id: string): boolean;
  collectAllDescendants(groupId: string, out: Set<string>, guard: Set<string>): void;
  groupLeafIds(groupId: string): string[];
  coMoveDescendants(parent: PlacedObject, newPos: THREE.Vector3, newRot: THREE.Quaternion): void;
  updateGroupBoxes(): void;
  pruneEmptyGroups(): boolean;
}

/**
 * Entity lookup and group/transform-hierarchy helpers over the live object /
 * emitter fields: id→entity resolution, transform-parent chain walks, group
 * descendant collection, group batch edits, subtree co-move, and the per-frame
 * group-box / empty-group passes.
 */
export function initEntityIndex(ctx: AppContext, deps: EntityIndexDeps): EntityIndex {
  const { field, emitterField, graph } = ctx;

  function objectById(id: string): PlacedObject | null {
    for (const o of field.list()) {
      if (o.id === id) {
        return o;
      }
    }
    return null;
  }

  function emitterById(id: string): Emitter | null {
    for (const e of emitterField.list()) {
      if (e.id === id) {
        return e;
      }
    }
    return null;
  }

  function isGroup(id: string): boolean {
    const o = objectById(id);
    return o !== null && o.type === "group";
  }

  function isOrbit(id: string): boolean {
    const o = objectById(id);
    return o !== null && o.type === "orbit";
  }

  function isTransformParent(id: string): boolean {
    return isGroup(id) || isOrbit(id);
  }

  function groupChildIds(groupId: string): string[] {
    return graph.edgesFrom(groupId)
      .filter((e: { fromPort: string }): boolean => e.fromPort === "transformOut")
      .map((e: { to: string }): string => e.to);
  }

  function transformParentId(id: string): string | null {
    const edges = graph.edgesTo(id, "transformIn");
    return edges.length > 0 ? edges[0].from : null;
  }

  /**
   * The highest group ancestor of an entity by walking transform-in parents, or null
   * if no ancestor is a group. A click on a group member resolves to this so the whole
   * cluster is grabbed; deeper members are reached only via terminal-click (item 6).
   * Grouping is independent of the node category, so this resolves regardless of it.
   */
  function topmostGroupOf(id: string): string | null {
    let top: string | null = null;
    let cur = transformParentId(id);
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      if (isGroup(cur)) {
        top = cur;
      }
      cur = transformParentId(cur);
    }
    return top;
  }

  /**
   * Whether `id` lies in the subtree of the currently-selected group (selected group is
   * an ancestor via transform-in). Used to relax selection gating: while a group is
   * selected, clicking one of its members selects the member directly instead of
   * redirecting to the group, so children become reachable by canvas click.
   */
  function selectedGroupContains(id: string): boolean {
    const sel = field.selectedObject();
    if (!sel || sel.type !== "group") {
      return false;
    }
    let cur = transformParentId(id);
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      if (cur === sel.id) {
        return true;
      }
      seen.add(cur);
      cur = transformParentId(cur);
    }
    return false;
  }

  function collectGroupLeaves(groupId: string, out: Set<string>, guard: Set<string>): void {
    if (guard.has(groupId)) {
      return;
    }
    guard.add(groupId);
    for (const childId of groupChildIds(groupId)) {
      if (isGroup(childId)) {
        collectGroupLeaves(childId, out, guard);
      } else {
        out.add(childId);
      }
    }
  }

  function collectAllDescendants(groupId: string, out: Set<string>, guard: Set<string>): void {
    if (guard.has(groupId)) {
      return;
    }
    guard.add(groupId);
    for (const childId of groupChildIds(groupId)) {
      out.add(childId);
      if (isTransformParent(childId)) {
        collectAllDescendants(childId, out, guard);
      }
    }
  }

  function groupLeafIds(groupId: string): string[] {
    const leaves = new Set<string>();
    collectGroupLeaves(groupId, leaves, new Set<string>());
    return [...leaves];
  }

  const coMoveDelta = new THREE.Quaternion();
  const coMoveTmp = new THREE.Vector3();

  /**
   * Rigidly transforms a transform parent's whole subtree (every descendant entity,
   * sub-group, and sub-orbit) by the delta from the parent's current base pose to a new
   * one, mutating each descendant's base position/rotation. Called before the parent
   * node's own base is moved, so a drag / gizmo-rotate co-moves its members; the
   * transform pass then reflects the new bases onto meshes and the SDF.
   */
  function coMoveDescendants(parent: PlacedObject, newPos: THREE.Vector3, newRot: THREE.Quaternion): void {
    coMoveDelta.copy(parent.rotation).invert().premultiply(newRot);
    const ids = new Set<string>();
    collectAllDescendants(parent.id, ids, new Set<string>());
    for (const id of ids) {
      const o = objectById(id);
      const em = o ? null : emitterById(id);
      const pos = o ? o.position : em ? em.position : null;
      const rot = o ? o.rotation : em ? em.rotation : null;
      if (!pos || !rot) {
        continue;
      }
      deps.recordBefore(o ? "obj" : "emitter", id);
      coMoveTmp.copy(pos).sub(parent.position).applyQuaternion(coMoveDelta).add(newPos);
      pos.copy(coMoveTmp);
      rot.premultiply(coMoveDelta);
    }
  }

  const groupBox = new THREE.Box3();
  const groupQuatInv = new THREE.Quaternion();
  const groupCorner = new THREE.Vector3();
  const groupCenter = new THREE.Vector3();
  const groupSize = new THREE.Vector3();

  /**
   * Drives every group node's pick/wireframe box to an oriented bounding box of its leaf
   * descendants (groups themselves carry no body): the members' world corners are folded
   * into the group's live frame (live rotation inverted), so the AABB is measured in the
   * group's own pre-rotation space and the box then rides the group's live pose. The box
   * therefore rotates with the cluster under an orbit parent or a gizmo rotate, instead
   * of staying world-axis-aligned. Only currently-visible members contribute, so a group
   * whose members are all hidden (a signal-node-only group while the node feature is off)
   * folds to an empty box and is deactivated — neither pickable nor halo-able.
   */
  function updateGroupBoxes(): void {
    for (const o of field.list()) {
      if (o.type !== "group") {
        continue;
      }
      const groupPos = field.liveOrBasePosition(o.id);
      const groupQuat = field.liveOrBaseRotation(o.id);
      if (!groupPos || !groupQuat) {
        continue;
      }
      groupQuatInv.copy(groupQuat).invert();
      const leaves = new Set<string>();
      collectGroupLeaves(o.id, leaves, new Set<string>());
      groupBox.makeEmpty();
      for (const id of leaves) {
        const mesh = field.meshById(id) ?? emitterField.meshById(id);
        if (!mesh || !mesh.visible) {
          continue;
        }
        mesh.updateWorldMatrix(true, false);
        if (!mesh.geometry.boundingBox) {
          mesh.geometry.computeBoundingBox();
        }
        const bb = mesh.geometry.boundingBox;
        if (!bb) {
          continue;
        }
        for (let c = 0; c < 8; c++) {
          groupCorner.set(c & 1 ? bb.max.x : bb.min.x, c & 2 ? bb.max.y : bb.min.y, c & 4 ? bb.max.z : bb.min.z);
          groupCorner.applyMatrix4(mesh.matrixWorld).sub(groupPos).applyQuaternion(groupQuatInv);
          groupBox.expandByPoint(groupCorner);
        }
      }
      if (groupBox.isEmpty()) {
        field.setGroupInactive(o.id);
        continue;
      }
      groupBox.getCenter(groupCenter);
      groupBox.getSize(groupSize);
      groupCenter.applyQuaternion(groupQuat).add(groupPos);
      field.setGroupBox(o.id, groupCenter, groupQuat, groupSize);
    }
  }

  /**
   * Removes any group left with zero members by a wiring disconnect — a group with no
   * children carries no meaning. Returns whether anything was removed so the caller can
   * persist / rebind. Not undo-tracked itself: the disconnect that emptied the group
   * already snapshotted the group + edge, so undo restores both.
   */
  function pruneEmptyGroups(): boolean {
    let removed = false;
    for (const o of [...field.list()]) {
      if (o.type !== "group") {
        continue;
      }
      if (groupChildIds(o.id).length <= 1) {
        graph.removeEntity(o.id);
        field.removeById(o.id);
        removed = true;
      }
    }
    return removed;
  }

  return {
    objectById,
    emitterById,
    isTransformParent,
    transformParentId,
    topmostGroupOf,
    selectedGroupContains,
    collectAllDescendants,
    groupLeafIds,
    coMoveDescendants,
    updateGroupBoxes,
    pruneEmptyGroups,
  };
}
